import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Footer from '../components/Footer';
import { ShoppingBag, Search, Mail, Phone, MapPin, Shield } from 'lucide-react';
import { productCategories, allProducts } from '../data/publicProducts';

const PublicHome = () => {
  const [searchAmount, setSearchAmount] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showResults, setShowResults] = useState(false);

  const handleSearch = () => {
    const amount = parseFloat(searchAmount);
    if (isNaN(amount) || amount < 1 || amount > 130) {
      alert('Please enter an amount between $1 and $130');
      return;
    }

    // Find products within +/- $5 and sort by exact match
    const matching = allProducts.filter(product => {
      const diff = Math.abs(product.price - amount);
      return diff <= 5;
    });

    const sorted = matching.sort((a, b) => {
      const diffA = Math.abs(a.price - amount);
      const diffB = Math.abs(b.price - amount);
      return diffA - diffB;
    });

    setFilteredProducts(sorted);
    setShowResults(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShoppingBag className="h-8 w-8 text-indigo-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">DressPay</h1>
                <p className="text-sm text-gray-600">Quality Products, Great Prices</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/terms">
                <Button variant="ghost" size="sm">Policies</Button>
              </Link>
              <Link to="/login">
                <Button variant="outline">Login</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl font-bold mb-6">Welcome to DressPay</h2>
          <p className="text-2xl mb-4">You Shop Here, We Serve You Good Quality</p>
          <p className="text-xl mb-8 text-indigo-100">Makeup • Toys • Baby Clothes • Diapers • Fashion</p>
          
          {/* Business Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
              <Mail className="h-8 w-8 mb-3 mx-auto" />
              <p className="font-semibold">Email Us</p>
              <a href="mailto:support@dresspay.com" className="text-sm text-indigo-100 hover:text-white">
                support@dresspay.com
              </a>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
              <Phone className="h-8 w-8 mb-3 mx-auto" />
              <p className="font-semibold">Call Us</p>
              <a href="tel:+1234567890" className="text-sm text-indigo-100 hover:text-white">
                +1 (234) 567-890
              </a>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
              <MapPin className="h-8 w-8 mb-3 mx-auto" />
              <p className="font-semibold">Online Store</p>
              <p className="text-sm text-indigo-100">
                Nationwide Shipping<br />Shop from Anywhere
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Search Products by Amount</h3>
            <p className="text-gray-600">Enter how much you want to spend, and we'll show you matching products</p>
          </div>
          
          <Card className="shadow-lg">
            <CardContent className="p-6">
              <div className="flex gap-4">
                <Input
                  type="number"
                  min="1"
                  max="130"
                  step="0.01"
                  value={searchAmount}
                  onChange={(e) => setSearchAmount(e.target.value)}
                  placeholder="Enter amount (e.g., 5, 25, 100)"
                  className="text-lg"
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch} size="lg" className="px-8">
                  <Search className="h-5 w-5 mr-2" />
                  Search
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Search Results */}
          {showResults && filteredProducts.length > 0 && (
            <div className="mt-8">
              <h4 className="text-2xl font-bold mb-6">
                {filteredProducts[0].price === parseFloat(searchAmount) 
                  ? `Exact Match: $${searchAmount}` 
                  : `Products near $${searchAmount}`}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {filteredProducts.map((product) => (
                  <Card key={product.id} className="hover-lift">
                    <div className="aspect-square overflow-hidden rounded-t-lg">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <CardContent className="p-4">
                      <Badge variant="secondary" className="mb-2">{product.category}</Badge>
                      <h5 className="font-semibold mb-1">{product.name}</h5>
                      <p className="text-sm text-gray-600 mb-3">{product.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-2xl font-bold text-indigo-600 number-display">
                          ${product.price.toFixed(2)}
                        </span>
                        {product.price === parseFloat(searchAmount) && (
                          <Badge className="bg-green-500">Exact Match!</Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {showResults && filteredProducts.length === 0 && (
            <div className="mt-8 text-center text-gray-500 py-12">
              <p className="text-xl">No products found near ${searchAmount}</p>
              <p>Try searching for amounts between $1 and $130</p>
            </div>
          )}
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-center mb-12">Shop by Category</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {Object.entries(productCategories).map(([key, category]) => (
              <Card key={key} className="hover-lift cursor-pointer">
                <CardContent className="p-6 text-center">
                  <h4 className="text-xl font-bold mb-2">{category.name}</h4>
                  <p className="text-indigo-600 font-semibold number-display mb-4">{category.range}</p>
                  <p className="text-sm text-gray-600">{category.products.length} products available</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-center mb-12">Featured Products</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[allProducts[0], allProducts[10], allProducts[20], allProducts[30]].filter(Boolean).map((product) => (
              <Card key={product.id} className="hover-lift">
                <div className="aspect-square overflow-hidden rounded-t-lg">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-4">
                  <Badge variant="secondary" className="mb-2">{product.category}</Badge>
                  <h5 className="font-semibold mb-1">{product.name}</h5>
                  <p className="text-sm text-gray-600 mb-3">{product.description}</p>
                  <span className="text-2xl font-bold text-indigo-600 number-display">
                    ${product.price.toFixed(2)}
                  </span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Payment Methods */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl font-bold mb-6">Secure Payment Methods</h3>
          <p className="text-gray-600 mb-8">We accept all major payment methods</p>
          <div className="flex flex-wrap justify-center gap-6 items-center">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="font-semibold">Credit Cards</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="font-semibold">Debit Cards</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="font-semibold">Apple Pay</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="font-semibold">Cash App</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="font-semibold">Cryptocurrency</p>
            </div>
          </div>
          <p className="mt-8 text-sm text-gray-500">All payments processed securely by Stripe</p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PublicHome;
